import React, { useState, useEffect } from "react";
import { DataSource, Connection } from "../../../data/models";
import "./ConnectionConfigModal.scss";

interface FieldConfig {
  name: string;
  label: string;
  type: "text" | "password" | "select" | "number";
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: { value: string; label: string }[];
  defaultValue?: string;
}

interface ConnectionConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: { name: string; config: Record<string, string> }) => void;
  dataSource: DataSource | null;
  isEditMode?: boolean;
  existingConnection?: Connection | null;
}

// Configuration fields for each data source type
const getFieldsForDataSource = (dataSourceType: string): FieldConfig[] => {
  const commonFields: FieldConfig[] = [
    {
      name: "configurationName",
      label: "Configuration Name",
      type: "text",
      required: true,
      placeholder: "Enter configuration name"
    }
    // {
    //   name: "connectionName",
    //   label: "Connection Name",
    //   type: "text",
    //   required: true,
    //   placeholder: "Enter connection name"
    // }
  ];

  switch (dataSourceType) {
    case "snowflake":
      return [
        ...commonFields,
        // {
        //   name: "accountIdentifier",
        //   label: "Account Identifier",
        //   type: "text",
        //   required: true,
        //   placeholder: "e.g., xy12345.us-east-1",
        //   helpText:
        //     "Your Snowflake account identifier (without .snowflakecomputing.com)"
        // },
        {
          name: "username",
          label: "Username",
          type: "text",
          required: true,
          placeholder: "Enter username"
        },
        {
          name: "password",
          label: "Password",
          type: "password",
          required: true,
          placeholder: "Enter password"
        },
        {
          name: "account",
          label: "Account",
          type: "text",
          required: true,
          placeholder: "e.g., xy12345.us-east-1"
          // helpText:
          //   "Your Snowflake account identifier (without .snowflakecomputing.com)"
        },
        {
          name: "warehouse",
          label: "Warehouse",
          type: "text",
          required: true,
          placeholder: "e.g., COMPUTE_WH"
        },
        {
          name: "database",
          label: "Database",
          type: "text",
          required: true,
          placeholder: "e.g., ANALYTICS_DB"
        },

        {
          name: "schema",
          label: "Schema",
          type: "text",
          required: true,
          placeholder: "e.g., PUBLIC"
          //   defaultValue: "PUBLIC"
        }
        // {
        //   name: "role",
        //   label: "Role",
        //   type: "text",
        //   required: false,
        //   placeholder: "e.g., ANALYST_ROLE (optional)"
        // },
        // {
        //   name: "region",
        //   label: "Region",
        //   type: "select",
        //   required: false,
        //   defaultValue: "us-east-1",
        //   options: [
        //     { value: "us-east-1", label: "US East (N. Virginia)" },
        //     { value: "us-west-2", label: "US West (Oregon)" },
        //     { value: "eu-west-1", label: "EU (Ireland)" },
        //     { value: "eu-central-1", label: "EU (Frankfurt)" },
        //     { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
        //     { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" }
        //   ]
        // }
      ];

    case "postgres":
      return [
        ...commonFields,
        {
          name: "host",
          label: "Host",
          type: "text",
          required: true,
          placeholder: "e.g., localhost or postgres.example.com"
        },
        {
          name: "port",
          label: "Port",
          type: "number",
          required: true,
          placeholder: "5432",
          defaultValue: "5432"
        },
        {
          name: "database",
          label: "Database",
          type: "text",
          required: true,
          placeholder: "Database name"
        },
        {
          name: "username",
          label: "Username",
          type: "text",
          required: true,
          placeholder: "Enter username"
        },
        {
          name: "password",
          label: "Password",
          type: "password",
          required: true,
          placeholder: "Enter password"
        }
      ];

    case "mysql":
      return [
        ...commonFields,
        {
          name: "host",
          label: "Host",
          type: "text",
          required: true,
          placeholder: "e.g., localhost or mysql.example.com"
        },
        {
          name: "port",
          label: "Port",
          type: "number",
          required: true,
          placeholder: "3306",
          defaultValue: "3306"
        },
        {
          name: "database",
          label: "Database",
          type: "text",
          required: true,
          placeholder: "Database name"
        },
        {
          name: "username",
          label: "Username",
          type: "text",
          required: true,
          placeholder: "Enter username"
        },
        {
          name: "password",
          label: "Password",
          type: "password",
          required: true,
          placeholder: "Enter password"
        }
      ];

    case "mongodb":
      return [
        ...commonFields,
        {
          name: "connectionString",
          label: "Connection String",
          type: "text",
          required: true,
          placeholder: "mongodb://localhost:27017/database",
          helpText:
            "MongoDB connection string (e.g., mongodb://localhost:27017/mydb)"
        },
        {
          name: "username",
          label: "Username",
          type: "text",
          required: false,
          placeholder: "Enter username (optional)"
        },
        {
          name: "password",
          label: "Password",
          type: "password",
          required: false,
          placeholder: "Enter password (optional)"
        }
      ];

    case "s3":
      return [
        ...commonFields,
        {
          name: "accessKeyId",
          label: "Access Key ID",
          type: "text",
          required: true,
          placeholder: "Enter AWS Access Key ID"
        },
        {
          name: "secretAccessKey",
          label: "Secret Access Key",
          type: "password",
          required: true,
          placeholder: "Enter AWS Secret Access Key"
        },
        {
          name: "region",
          label: "Region",
          type: "select",
          required: true,
          defaultValue: "us-east-1",
          options: [
            { value: "us-east-1", label: "US East (N. Virginia)" },
            { value: "us-west-1", label: "US West (N. California)" },
            { value: "us-west-2", label: "US West (Oregon)" },
            { value: "eu-west-1", label: "EU (Ireland)" },
            { value: "eu-central-1", label: "EU (Frankfurt)" },
            { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" }
          ]
        },
        {
          name: "bucket",
          label: "Bucket Name",
          type: "text",
          required: false,
          placeholder: "Enter S3 bucket name (optional)"
        }
      ];

    case "api":
      return [
        ...commonFields,
        {
          name: "baseUrl",
          label: "Base URL",
          type: "text",
          required: true,
          placeholder: "https://api.example.com"
        },
        {
          name: "authType",
          label: "Authentication Type",
          type: "select",
          required: true,
          defaultValue: "none",
          options: [
            { value: "none", label: "None" },
            { value: "bearer", label: "Bearer Token" },
            { value: "basic", label: "Basic Auth" },
            { value: "apikey", label: "API Key" }
          ]
        },
        {
          name: "apiKey",
          label: "API Key / Token",
          type: "password",
          required: false,
          placeholder: "Enter API key or token"
        }
      ];

    case "sharepoint":
      return [
        ...commonFields,
        {
          name: "siteUrl",
          label: "SharePoint Site URL",
          type: "text",
          required: true,
          placeholder: "https://company.sharepoint.com/sites/sitename"
        },
        {
          name: "clientId",
          label: "Client ID",
          type: "text",
          required: true,
          placeholder: "Enter Azure App Client ID"
        },
        {
          name: "clientSecret",
          label: "Client Secret",
          type: "password",
          required: true,
          placeholder: "Enter Azure App Client Secret"
        },
        {
          name: "tenantId",
          label: "Tenant ID",
          type: "text",
          required: true,
          placeholder: "Enter Azure Tenant ID"
        }
      ];

    default:
      return commonFields;
  }
};

const ConnectionConfigModal: React.FC<ConnectionConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  dataSource,
  isEditMode = false,
  existingConnection = null
}) => {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<FieldConfig[]>([]);

  useEffect(() => {
    if (dataSource) {
      const fieldsForSource = getFieldsForDataSource(dataSource.type);
      setFields(fieldsForSource);

      // Initialize config with default values or existing connection data
      const initialConfig: Record<string, string> = {};

      if (isEditMode && existingConnection) {
        // Pre-fill with existing connection data
        fieldsForSource.forEach((field) => {
          if (field.name === "configurationName") {
            initialConfig[field.name] = existingConnection.name;
          } else if (field.name === "connectionName") {
            initialConfig[field.name] = existingConnection.name;
          } else if (
            existingConnection.config &&
            existingConnection.config[field.name]
          ) {
            initialConfig[field.name] = existingConnection.config[field.name];
          } else if (field.defaultValue) {
            initialConfig[field.name] = field.defaultValue;
          } else {
            initialConfig[field.name] = "";
          }
        });
      } else {
        // Initialize with default values for new connection
        fieldsForSource.forEach((field) => {
          if (field.defaultValue) {
            initialConfig[field.name] = field.defaultValue;
          } else {
            initialConfig[field.name] = "";
          }
        });
      }

      setConfig(initialConfig);
    }
  }, [dataSource, isEditMode, existingConnection]);

  const handleInputChange = (fieldName: string, value: string) => {
    setConfig((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSave = () => {
    if (!dataSource) return;

    onSave({
      name: config.configurationName,
      config: config
    });
    onClose();
    setConfig({});
  };

  const isFormValid = () => {
    return fields
      .filter((field) => field.required)
      .every((field) => config[field.name] && config[field.name].trim() !== "");
  };

  if (!isOpen || !dataSource) return null;

  const renderField = (field: FieldConfig) => {
    switch (field.type) {
      case "select":
        return (
          <select
            id={field.name}
            value={config[field.name] || ""}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <div className="input-wrapper">
            <input
              id={field.name}
              type={field.type}
              placeholder={field.placeholder}
              value={config[field.name] || ""}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
            {config[field.name] && (
              <button
                className="clear-button"
                onClick={() => handleInputChange(field.name, "")}
              >
                ×
              </button>
            )}
          </div>
        );
    }
  };

  return (
    <div className="modal-overlay">
      <div className="connection-modal">
        <div className="modal-header">
          <div className="header-content">
            <div className="icon-wrapper">
              <span className="data-source-icon">{dataSource.icon}</span>
            </div>
            <div>
              <h2>
                {isEditMode ? "Edit" : "Configure"} {dataSource.name} Connection
              </h2>
              <p>
                {isEditMode
                  ? "Update your connection to"
                  : "Set up your connection to"}{" "}
                {dataSource.name}
              </p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="modal-content">
          {fields.map((field, index) => (
            <div key={field.name} className="form-group">
              <label htmlFor={field.name}>
                {field.label}{" "}
                {field.required && <span className="required">*</span>}
              </label>
              {renderField(field)}
              {field.helpText && (
                <small className="help-text">{field.helpText}</small>
              )}
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={!isFormValid()}
          >
            {isEditMode ? "Update Configuration" : "Save Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectionConfigModal;
